import { useState, useEffect } from 'react';
import type { Event } from '@buzzmap/shared';
import { takeSsrEvent } from '../utils/ssrBootstrap';
import { isSsrRootReleased, subscribeSsrRootRelease } from '../utils/ssrShell';
import { useEmbeddedClaim } from './useEmbeddedClaim';

interface UseEventResult {
  event: Event | null;
  loading: boolean;
  error: string | null;
  /**
   * True when the API withheld this event with the structured 409
   * `DATE_UNDER_REVIEW` contract (T009-BE, plan R7): the event exists but
   * its disputed date is under verification. A distinct third state — not
   * an error, not a 404 — so the caller shows the review notice instead of
   * the "not found" block. In this state `event` and `error` are both null:
   * any previously held event data for the id has been cleared.
   */
  dateUnderReview: boolean;
}

/**
 * Whole-code match against the T009 409 body
 * (`{ code: 'DATE_UNDER_REVIEW', message: string }`). A 409 with any other
 * (or no) code is NOT the review state and falls through to the generic
 * error path; a body that fails to parse counts as "not the review state"
 * (fail-closed toward the existing error behavior).
 *
 * Exported for reuse by any client that consumes the same by-ID endpoint —
 * notably `SavedEventsPage`, which must keep a date-held favorite listed
 * instead of silently dropping it (T010-FE).
 */
export async function isDateUnderReviewResponse(response: Response): Promise<boolean> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return false;
  }
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { code?: unknown }).code === 'DATE_UNDER_REVIEW'
  );
}

/** What `/api/events?id=` said about one event. */
type EventAnswer =
  | { kind: 'event'; event: Event }
  | { kind: 'review' }
  | { kind: 'failed'; status: number | null; message: string };

/**
 * The one request for an event by id, shared by the normal load and by the
 * revalidation of an embedded body. A thrown network error or an unreadable
 * 200 body is `failed` with `status: null`.
 */
async function requestEvent(id: string, signal?: AbortSignal): Promise<EventAnswer> {
  try {
    const url = `/api/events?id=${encodeURIComponent(id)}`;
    const response = await (signal ? fetch(url, { signal }) : fetch(url));
    if (!response.ok) {
      if (response.status === 404) {
        return { kind: 'failed', status: 404, message: 'Evento non trovato' };
      }
      if (response.status === 409 && (await isDateUnderReviewResponse(response))) {
        return { kind: 'review' };
      }
      return {
        kind: 'failed',
        status: response.status,
        message: `Errore nel caricamento: ${response.status}`,
      };
    }
    return { kind: 'event', event: await response.json() };
  } catch (err) {
    return {
      kind: 'failed',
      status: null,
      message: err instanceof Error ? err.message : 'Errore sconosciuto',
    };
  }
}

/** Statuses that, on revalidation, mean the event is no longer there to show. */
const GONE_STATUSES = new Set([404, 410]);

/** Longest wait for an idle moment before the confirmation request goes anyway. */
const IDLE_TIMEOUT_MS = 2000;

/**
 * Longest wait for the page `load` event, counted from the root release (same cap as the PostHog
 * download): a slow or stuck third-party photo must not hold the date confirmation back forever.
 */
const MAX_WAIT_FOR_LOAD_MS = 4000;

/**
 * Run `task` once the server shell has handed over to the app (root released), the page has
 * finished loading (its photos included) and the browser is idle, so the confirmation request
 * never competes with the first paint or the LCP resource. Returns the cancel function.
 */
function afterReleaseAndIdle(task: () => void): () => void {
  let cancelIdle: (() => void) | null = null;
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;
  let released = isSsrRootReleased();
  let loaded = document.readyState === 'complete';
  let loadCapTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleIdle = () => {
    if (cancelled || cancelIdle || !released) return;
    if (!loaded) {
      loadCapTimer ??= setTimeout(() => {
        loaded = true;
        scheduleIdle();
      }, MAX_WAIT_FOR_LOAD_MS);
      return;
    }
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(task, { timeout: IDLE_TIMEOUT_MS });
      cancelIdle = () => window.cancelIdleCallback(handle);
    } else {
      const handle = window.setTimeout(task, 0);
      cancelIdle = () => window.clearTimeout(handle);
    }
  };
  const onLoad = () => {
    loaded = true;
    scheduleIdle();
  };

  if (!released) {
    unsubscribe = subscribeSsrRootRelease(() => {
      unsubscribe?.();
      unsubscribe = null;
      released = true;
      scheduleIdle();
    });
  }
  if (!loaded) window.addEventListener('load', onLoad, { once: true });
  scheduleIdle();

  return () => {
    cancelled = true;
    unsubscribe?.();
    window.removeEventListener('load', onLoad);
    if (loadCapTimer) clearTimeout(loadCapTimer);
    cancelIdle?.();
  };
}

/**
 * Hook for fetching a single event by ID.
 * @param id - The event ID to fetch
 * @returns Object containing event data, loading state, error state, and the
 *          date-under-review flag (T010-FE)
 */
export function useEvent(id: string): UseEventResult {
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateUnderReview, setDateUnderReview] = useState(false);
  const embeddedEvent = useEmbeddedClaim<Event>();

  useEffect(() => {
    let cancelled = false;
    let cancelRevalidation: (() => void) | null = null;
    const abort = new AbortController();

    // T208: the embedded body can be up to a cache lifetime old, but the API stays
    // authoritative on the date (T010 R7). Only an explicit review/gone answer or a
    // different 200 body changes the view; a network error or 5xx keeps it.
    async function revalidate(embedded: Event) {
      const answer = await requestEvent(id, abort.signal);
      if (cancelled) return;
      if (answer.kind === 'review') {
        setEvent(null);
        setError(null);
        setDateUnderReview(true);
      } else if (answer.kind === 'failed') {
        if (answer.status !== null && GONE_STATUSES.has(answer.status)) {
          setError(answer.message);
          setEvent(null);
        }
      } else if (
        answer.event?.id === id &&
        JSON.stringify(answer.event) !== JSON.stringify(embedded)
      ) {
        setEvent(answer.event);
      }
    }

    async function fetchEvent() {
      // Per-request resets sit above the `!id` guard so no state — least of
      // all the review flag — can survive a transition to an empty id
      // (T010-FE S2). A late response for a previous id is dropped by the
      // `cancelled` guard below.
      setLoading(true);
      setError(null);
      setEvent(null);
      setDateUnderReview(false);

      if (!id) {
        setError('ID evento non valido');
        setLoading(false);
        return;
      }

      // T204-INT: the SSR document of this very event page carries the body of
      // `/api/events?id=`. One-shot, and only for the id it was built for.
      const embedded = embeddedEvent.claim(id, () => takeSsrEvent(id));
      if (embedded) {
        setEvent(embedded);
        setLoading(false);
        cancelRevalidation = afterReleaseAndIdle(() => void revalidate(embedded));
        return;
      }
      embeddedEvent.release();

      const answer = await requestEvent(id);
      if (cancelled) return;
      if (answer.kind === 'review') {
        // Authoritative review answer: no previous event data may
        // survive in state, and this is not an "error" to surface.
        setEvent(null);
        setError(null);
        setDateUnderReview(true);
      } else if (answer.kind === 'failed') {
        setError(answer.message);
        setEvent(null);
      } else {
        setEvent(answer.event);
      }
      setLoading(false);
    }

    fetchEvent();

    return () => {
      cancelled = true;
      cancelRevalidation?.();
      abort.abort();
    };
  }, [id, embeddedEvent]);

  return { event, loading, error, dateUnderReview };
}
